import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

/**
 * LeadFormModal — แบบฟอร์มขอใบเสนอราคา/นัดหมาย
 * เก็บข้อมูลแบบ multi-step → ส่งผ่าน WhatsApp/LINE/Email พร้อมข้อความที่ฟอร์แมตแล้ว
 */

const PHONE = "+66832494999";
const WA_BASE = "https://wa.me/66832494999";
const LINE_URL = "https://line.me/ti/p/@nycli";
const EMAIL = "contact@nyclegal.co.th";

const SERVICES = [
  "แปลเอกสาร · รับรองกงสุล (MFA)",
  "Notary Public · รับรองเอกสาร",
  "วีซ่า · LTR · Elite · Smart Visa",
  "จดทะเบียนบริษัท · BOI · FBL",
  "ทนายความ · คดีความ · อนุญาโตตุลาการ",
  "M&A · Due Diligence · Corporate",
  "อื่น ๆ (ระบุในรายละเอียด)",
];

const URGENCY = ["ภายใน 24 ชม. (ด่วนมาก)", "ภายใน 3 วัน", "ภายใน 1 สัปดาห์", "ยังไม่ด่วน · สอบถามข้อมูล"];

const schema = z.object({
  name: z.string().trim().min(2, "กรุณาระบุชื่อ-นามสกุล").max(80),
  phone: z
    .string()
    .trim()
    .min(8, "เบอร์โทรไม่ถูกต้อง")
    .max(20)
    .regex(/^[0-9+\-\s()]+$/, "เบอร์โทรมีอักขระไม่อนุญาต"),
  email: z.string().trim().email("อีเมลไม่ถูกต้อง").max(120).optional().or(z.literal("")),
  service: z.string().min(1, "กรุณาเลือกบริการ"),
  urgency: z.string().min(1, "กรุณาเลือกความเร่งด่วน"),
  details: z.string().trim().max(1000, "รายละเอียดยาวเกินไป").optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

function buildMessage(v: FormValues) {
  return [
    "🔔 *ขอใบเสนอราคา · NYC Legal & Notary*",
    "",
    `👤 ชื่อ: ${v.name}`,
    `📱 เบอร์โทร: ${v.phone}`,
    v.email ? `📧 อีเมล: ${v.email}` : null,
    `📂 บริการ: ${v.service}`,
    `⏰ ความเร่งด่วน: ${v.urgency}`,
    v.details ? `📝 รายละเอียด:\n${v.details}` : null,
    "",
    "— ส่งจากเว็บไซต์ nyclanguageinstitute.com",
  ]
    .filter(Boolean)
    .join("\n");
}

export type LeadFormModalProps = {
  trigger?: React.ReactNode;
  defaultService?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function LeadFormModal({ trigger, defaultService, defaultOpen, onOpenChange }: LeadFormModalProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [submitted, setSubmitted] = useState(false);
  const [lastValues, setLastValues] = useState<FormValues | null>(null);
  const startedRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      service: defaultService ?? "",
      urgency: "",
      details: "",
    },
  });

  const service = watch("service");
  const urgency = watch("urgency");

  const handleClose = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
    if (next) {
      track("lead_form_open", { default_service: defaultService || "" });
      startedRef.current = false;
    } else {
      if (!submitted) track("lead_form_abandon");
      setTimeout(() => {
        setSubmitted(false);
        startedRef.current = false;
        reset();
      }, 200);
    }
  };

  const onFirstInteract = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("lead_form_start");
  };

  const onSubmit = (values: FormValues) => {
    setLastValues(values);
    setSubmitted(true);
    track("lead_submit", {
      service: values.service,
      urgency: values.urgency,
      has_email: values.email ? true : false,
      has_details: values.details ? true : false,
    });
    toast.info("อีก 1 ขั้นตอน — ยังไม่ได้ส่งข้อมูล", {
      description: "กดเลือกช่องทางติดต่อเพื่อส่งคำขอของคุณ",
    });
  };

  const message = lastValues ? buildMessage(lastValues) : "";
  const waHref = `${WA_BASE}?text=${encodeURIComponent(message)}`;
  const mailHref = `mailto:${EMAIL}?subject=${encodeURIComponent("ขอใบเสนอราคา NYC Legal & Notary")}&body=${encodeURIComponent(message)}`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="max-w-lg">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">ขอใบเสนอราคา · ปรึกษาฟรี</DialogTitle>
              <DialogDescription>กรอกข้อมูลสั้น ๆ ทีมงานตอบกลับภายใน 15 นาที (ในเวลาทำการ)</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} onFocusCapture={onFirstInteract} onChange={onFirstInteract} className="space-y-4" data-track-zone="lead_form">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="lf-name">ชื่อ-นามสกุล *</Label>
                  <Input id="lf-name" placeholder="คุณสมชาย ใจดี" {...register("name")} />
                  {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lf-phone">เบอร์โทร *</Label>
                  <Input id="lf-phone" inputMode="tel" placeholder="08X-XXX-XXXX" {...register("phone")} />
                  {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lf-email">อีเมล (ถ้ามี)</Label>
                <Input id="lf-email" type="email" placeholder="you@example.com" {...register("email")} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label>บริการที่สนใจ *</Label>
                <div className="flex flex-wrap gap-1.5">
                  {SERVICES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setValue("service", s, { shouldValidate: true })}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        service === s
                          ? "border-amber-500 bg-amber-500 text-navy-deep"
                          : "border-border bg-background text-muted-foreground hover:border-amber-400 hover:text-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {errors.service && <p className="text-xs text-destructive">{errors.service.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label>ความเร่งด่วน *</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {URGENCY.map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setValue("urgency", u, { shouldValidate: true })}
                      className={`rounded-md border px-3 py-2 text-xs font-medium transition ${
                        urgency === u
                          ? "border-amber-500 bg-amber-500/10 text-foreground"
                          : "border-border text-muted-foreground hover:border-amber-400"
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
                {errors.urgency && <p className="text-xs text-destructive">{errors.urgency.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lf-details">รายละเอียดเพิ่มเติม</Label>
                <Textarea id="lf-details" rows={3} placeholder="เช่น แปล Transcript ปริญญาตรี 4 หน้า ใช้ยื่นวีซ่าออสเตรเลีย" {...register("details")} />
                {errors.details && <p className="text-xs text-destructive">{errors.details.message}</p>}
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-500 text-navy-deep hover:bg-amber-400">
                {isSubmitting ? "กำลังส่ง..." : "ส่งคำขอ → เลือกช่องทางตอบกลับ"}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                การส่งฟอร์มถือว่ายอมรับ <a href="#" className="underline">นโยบายความเป็นส่วนตัว</a> · ข้อมูลถูกเก็บเป็นความลับ
              </p>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">อีก 1 ขั้นตอน · เลือกช่องทางเพื่อส่งคำขอ</DialogTitle>
              <DialogDescription>
                ข้อมูลของคุณยังไม่ถูกส่ง — กรุณากดเลือกช่องทางด้านล่าง (WhatsApp / LINE / โทร / อีเมล) เพื่อส่งข้อความที่จัดเตรียมไว้ให้เรา
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2" data-track-zone="lead_form_success">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between rounded-lg bg-[#25D366] px-4 py-3 text-white transition hover:bg-[#1FB855]"
              >
                <span className="font-semibold">ส่งทาง WhatsApp</span>
                <span className="text-xs opacity-90">ตอบใน 15 นาที</span>
              </a>
              <a
                href={LINE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between rounded-lg bg-[#04A03A] px-4 py-3 text-white transition hover:bg-[#05B14C]"
              >
                <span className="font-semibold">เพิ่มเพื่อนใน LINE @nycli</span>
                <span className="text-xs opacity-90">แชทสะดวก</span>
              </a>
              <a
                href={`tel:${PHONE}`}
                className="flex items-center justify-between rounded-lg bg-amber-500 px-4 py-3 text-navy-deep transition hover:bg-amber-400"
              >
                <span className="font-semibold">โทร 083-249-4999</span>
                <span className="text-xs opacity-90">เร็วที่สุด</span>
              </a>
              <a
                href={mailHref}
                className="flex items-center justify-between rounded-lg border border-border px-4 py-3 transition hover:bg-accent"
              >
                <span className="font-semibold">ส่งอีเมล contact@nyclegal.co.th</span>
                <span className="text-xs text-muted-foreground">เป็นทางการ</span>
              </a>
            </div>

            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="mb-2 text-xs font-semibold text-muted-foreground">ข้อความที่จะถูกส่ง:</p>
              <pre className="whitespace-pre-wrap break-words text-[11px] text-foreground/80">{message}</pre>
            </div>

            <Button variant="outline" onClick={() => handleClose(false)} className="w-full">
              ปิดหน้าต่าง
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
